import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import { AuthContext } from '../../authProvider.mjs';
import clarificationService from '../service/clarificationService.mjs';
import { CHANGED_EVENT } from '../util/clarificationText.mjs';

/**
 * The navbar's question bell: how many of the questions the reader is part of
 * have something new, and how many are waiting on their answer.
 *
 * Staff always see it. A claimant sees it only while there is something - a
 * question an admin has put to them, or a reply - so the navbar of everyone
 * who never gets a question is unchanged.
 *
 * Polls once a minute while the tab is visible, and refreshes at once when a
 * question changes on the page or the tab comes back into view - email links
 * bring people straight to a thread, so this is the in-app half of the
 * notification.
 */
const POLL_MS = 60000;

export const useIsStaff = () => {
  const auth = useContext(AuthContext);
  return !!(auth.isAuthenticated && (auth.isAdmin || auth.isAuditor || auth.isRegionalDirector
    || auth.isGlobalDirector || auth.isFinanceDirector || auth.isAuditorDelegate));
};

const ClarificationBell = ({ className = '' }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const isStaff = useIsStaff();
  const [counts, setCounts] = useState({ count: 0, needsAnswer: 0 });

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    try {
      setCounts(await clarificationService.unreadCount());
    } catch {
      // A missed poll just leaves the last count up.
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    window.addEventListener(CHANGED_EVENT, refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener(CHANGED_EVENT, refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [isAuthenticated, refresh]);

  const { count, needsAnswer } = counts;
  if (!isAuthenticated || (!isStaff && !count && !needsAnswer)) return null;

  const label = count > 0
    ? `Questions: ${count} with something new${needsAnswer ? `, ${needsAnswer} waiting on your answer` : ''}`
    : needsAnswer > 0 ? `Questions: ${needsAnswer} waiting on your answer` : 'Questions';
  const shown = count || needsAnswer;

  return (
    <button
      type="button"
      onClick={() => navigate('/clarifications')}
      aria-label={label}
      title={label}
      className={`relative inline-flex items-center gap-2 rounded-lg text-gray-700 hover:text-stanford-red hover:bg-gray-50 md:hover:bg-transparent ${className}`}
    >
      <FaBell className="w-5 h-5" aria-hidden="true" />
      <span className="md:sr-only">Questions</span>
      {shown > 0 && (
        <span className="absolute -top-1 left-3 min-w-[18px] h-[18px] px-1 rounded-full bg-stanford-red text-white text-[11px] font-bold leading-[18px] text-center tabular-nums">
          {shown > 99 ? '99+' : shown}
        </span>
      )}
    </button>
  );
};

export default ClarificationBell;
